/**
 * dogs.js
 */

import React from 'react';
import { useMediaQuery, Container, Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import Smartlook from 'smartlook-client';
import { ReactSVG } from 'react-svg';
import logger from 'utils/logger';
import analytics from 'utils/analytics';
import useUser from 'utils/use-user';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import PageTitle from 'components/page-title';
import PageTitleForUser from 'components/page-title-for-user';
import FeatureBox from 'components/feature-box';
import Card from 'components/card';
import Button from 'components/button';
import Icon from 'components/icon';
import BarButton from 'components/layout/app-bar/bar-button';
import emptySVG from 'assets/images/empty.svg';
import DogView from './view';
import DogEdit from './edit';
import { DogsContext, DogsProvider } from 'contexts/dogs/dogs-context';


const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  dogList: {
    '& .emptySVG svg': {
      display: 'block',
      width: '30%',
      maxWidth: theme.spacing(40),
      height: 'auto',
      margin: `${theme.spacing(8)} auto ${theme.spacing(4)}`,
      opacity: 0.5,
      '& .custom-fill': {
        fill: theme.palette.primary.colors[1],
      },
    }
  },
  cardContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0),
    },
  },
  noDogsSection: {
    textAlign: 'center',
  },
  noDogsHeading: {
    fontWeight: '600',
    paddingBottom: theme.spacing(1),
  },
  noDogsText: {
    color: `${theme.palette.grey.colors[5]}`,
  },
  titleActions: {
    '&> *' : {
      marginLeft: theme.spacing(2),
    }
  },
}));


export default function Dogs(props) {
  const { userId } = useParams();
  
  if (userId) {
    return <DogsProvider userId={userId}>
      <DogsImplementation {...props} />
    </DogsProvider>;
  } else {
    return <DogsImplementation {...props} />;
  }
}


function DogsImplementation(props) {
  const classes = useStyles();
  const { userId } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { user } = React.useContext(UserContext);
  const { dogs, updateDog, setDogs, error } = React.useContext(DogsContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const requestedUser = useUser(userId, false);
  const [title, setTitle] = React.useState();
  const [dogEdit, setDogEdit] = React.useState();
  const isSizeXS = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isReadOnly = userId > 0 && user.id !== userId && user.role.isTrainer;  
  const numUserExers = user?.completeExercisesCount;
  const numUserDeploys = user?.deploymentCount;
  const isNewUser = numUserExers === 0 && numUserDeploys === 0;
  const titleBase = 'Dogs';

  // init
  React.useEffect(() => {
    const actions = (user.role.isHandler || user.role.isSupervisor)
      ? [<BarButton
        data-testid='add-dog-button'
        children={<Icon type='add' />}
        onClick={() => {
          setDogEdit({});
          history.push(`${url}/add/details`);
        }}
      />]
      : [];

    document.title = `PACKTRACK - ${titleBase}`;
    const title = <PageTitleForUser title={titleBase} userName={requestedUser?.name || ''} />;
    setTitle(title);
    setAppBar(title, actions);
  }, [userId, user, requestedUser, history, url, setAppBar]);


  // Send a GA4 and Smartlook analytics event if this is a new/onboarding user
  React.useEffect(() => {
    if (isNewUser && dogs) {
      if (dogs.length === 0) {
        Smartlook.track("onboard_first_visit");
        analytics.trackGA4('event', { name: 'onboard_first_visit' });
        logger.logAnalytics('[dogs.js] Sending analytics event: onboard_first_visit');
      }
      if (dogs.length === 1) {
        Smartlook.track("onboard_dog_saved");
        analytics.trackGA4('event', { name: 'onboard_dog_saved' });
        logger.logAnalytics('[dogs.js] Sending analytics event: onboard_dog_saved');
      }
    }
  }, [dogs, isNewUser]);


  const onDogSaved = (dog) => {
    setDogEdit(dog);
    updateDog(dog);
  }

  return (
    <Container className={classes.root} data-testid='dogs-page'>

      {/* forbidden */}
      {error &&
      <Typography variant="h6">{error}</Typography>}

      <FeatureBox
        style={{ minHeight: 240 }}
        spinnerSize={60}
        loading={!dogs && !error}
      >
        {dogs &&
        <Box>

          {/* title */}
          {!isSizeXS &&
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <PageTitle iconName='dog-profile' title={title} />

            <Box className={classes.titleActions}>
              {(user.role.isHandler || user.role.isSupervisor) &&
              <Button
                variant="contained"
                aria-label="add dog"
                disabled={!dogs}
                data-testid='add-dog-button'
                startIcon={<Icon type='add'/>}
                children='Add Dog'
                onClick={() => {
                  setDogEdit({});
                  history.push(`${url}/add/details`);
                }}
              />}
            </Box>
          </Box>
          }
          <div className={classes.dogList}>

            {/* dog list */}
            {dogs &&
            <>
              {dogs.map((dog) =>
                <React.Fragment key={dog.id}>
                  <Box className={classes.cardContainer}>
                    <Card>
                      <DogView
                        dog={dog}
                        userId={userId}
                        onSave={onDogSaved}
                        setDogs={setDogs}
                        setDogEdit={setDogEdit}
                      />
                    </Card>
                  </Box>
                </React.Fragment>
              )}
            </>}

            {/* empty state */}
            {(dogs && dogs.length === 0) &&
            <>
              <Box className={classes.noDogsSection}>
                <ReactSVG src={emptySVG} className="emptySVG" />
                <Typography variant="h6" className={classes.noDogsHeading}>
                  No Dogs Found
                </Typography>
                <Typography variant="body2" className={classes.noDogsText}>
                  To use PACKTRACK you'll first need to configure your K9.<br/>
                  Click the Add Dog button above to get started.
                </Typography>
              </Box>
            </>}

          </div>

          {!isReadOnly &&
          <DogEdit
            onClose={() => setDogEdit()}
            onSave={onDogSaved}
            forceDefault={dogs.length === 0}
            userId={userId}
            dog={dogEdit}
            isDogCreate={dogEdit}
          />}

        </Box>}
      </FeatureBox>

    </Container>
  );
}